import Footer from "../../components/common/layout/Footer";
import ProductPlansDisplay from "@/components/features/shared/ProductPlansDisplay/ProductPlansDisplay";
import AnimatedSection from "../../components/common/animations/AnimatedSection";
import ParallaxSection from "../../components/common/animations/ParallaxSection";
import Floating3D from "../../components/common/animations/Floating3D";
import { useEffect, useState } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { motion } from "framer-motion";
import { Check, Server, Zap, Shield, Globe, Database, AlertTriangle } from "lucide-react";
import FAQSection from "../../components/common/sections/FAQ";
import { getWindowsServerPlans } from "@/services/dreamscapeApi";
import { CurrencyCode } from "@/utils/currencyConverter";
import Breadcrumbs from "@/components/common/sections/Breadcrumbs";
import BreadcrumbsImage from "@/assets/images/domain/windowsServers.png";
import SEO from "@/components/common/seo/seo";

if (typeof window !== 'undefined') {
    gsap.registerPlugin(ScrollTrigger);
}

export default function WindowsServer() {
    const [plans, setPlans] = useState<any[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedCurrency, setSelectedCurrency] = useState<CurrencyCode>('USD');
    const [isDummyData, setIsDummyData] = useState(false);

    useEffect(() => {
        // Load Windows Server plans from API
        const loadPlans = async () => {
            setLoading(true);
            setError(null);
            setIsDummyData(false);
            
            try {
                // Try to fetch real data from Dreamscape API (sandomains.com)
                const result = await getWindowsServerPlans({ limit: 50 });
                
                if (result.success && result.plans && result.plans.length > 0) {
                    setPlans(result.plans);
                    setIsDummyData(false);
                } else {
                    // If API returns no plans, fall back to dummy data
                    console.warn('No plans returned from API, using dummy data:', result.message);
                    setIsDummyData(true);
                    loadDummyPlans();
                }
            } catch (err) {
                // If API call fails, fall back to dummy data
                console.error('Failed to load plans from API, using dummy data:', err);
                setError(err instanceof Error ? err.message : 'Failed to load plans');
                setIsDummyData(true);
                loadDummyPlans();
            } finally {
                setLoading(false);
            }
        };

        // Fallback dummy data function
        const loadDummyPlans = () => {
            const dummyPlans = [
                {
                    id: 1,
                    name: "Windows Server Starter",
                    description: "Super Fast and Dedicated VPS Hosting. Perfect for small applications.",
                    periods: [
                        { period: 1, price: { register: 29.99, renew: 39.99 } },
                        { period: 12, price: { register: 24.99, renew: 34.99 } },
                        { period: 24, price: { register: 19.99, renew: 29.99 } },
                        { period: 36, price: { register: 14.99, renew: 24.99 } }
                    ],
                    features: [
                        { name: "CPU Cores", value: "2" },
                        { name: "RAM", value: "4 GB" },
                        { name: "Storage", value: "50 GB SSD" },
                        { name: "Bandwidth", value: "2 TB" },
                        { name: "Windows Server", value: "2019/2022" }
                    ]
                },
                {
                    id: 2,
                    name: "Windows Server Business",
                    description: "Ideal for growing businesses. More resources for your Windows applications.",
                    periods: [
                        { period: 1, price: { register: 59.99, renew: 79.99 } },
                        { period: 12, price: { register: 49.99, renew: 69.99 } },
                        { period: 24, price: { register: 39.99, renew: 59.99 } },
                        { period: 36, price: { register: 29.99, renew: 49.99 } }
                    ],
                    features: [
                        { name: "CPU Cores", value: "4" },
                        { name: "RAM", value: "8 GB" },
                        { name: "Storage", value: "100 GB SSD" },
                        { name: "Bandwidth", value: "4 TB" },
                        { name: "Windows Server", value: "2019/2022" }
                    ]
                },
                {
                    id: 3,
                    name: "Windows Server Professional",
                    description: "For professional applications. Maximum performance and resources.",
                    periods: [
                        { period: 1, price: { register: 99.99, renew: 129.99 } },
                        { period: 12, price: { register: 79.99, renew: 109.99 } },
                        { period: 24, price: { register: 69.99, renew: 99.99 } },
                        { period: 36, price: { register: 59.99, renew: 89.99 } }
                    ],
                    features: [
                        { name: "CPU Cores", value: "8" },
                        { name: "RAM", value: "16 GB" },
                        { name: "Storage", value: "200 GB SSD" },
                        { name: "Bandwidth", value: "8 TB" },
                        { name: "Windows Server", value: "2019/2022" }
                    ]
                }
            ];
            setPlans(dummyPlans);
        };

        loadPlans();
    }, []);

    useEffect(() => {
        // Animate sections on scroll
        const sections = document.querySelectorAll('.hosting-section');

        sections.forEach((section, index) => {
            gsap.fromTo(section,
                {
                    opacity: 0,
                    y: 60,
                    scale: 0.95
                },
                {
                    opacity: 1,
                    y: 0,
                    scale: 1,
                    duration: 0.8,
                    delay: index * 0.15,
                    ease: "power3.out",
                    scrollTrigger: {
                        trigger: section,
                        start: "top 80%",
                        toggleActions: "play none none none"
                    }
                }
            );
        });

        return () => {
            ScrollTrigger.getAll().forEach(trigger => trigger.kill());
        };
    }, [plans]);

    const handleSelectPlan = (plan: any, period: number, price: number) => {
        console.log('Selected WordPress hosting plan:', plan, 'Period:', period, 'Price:', price);
        // TODO: Implement cart/checkout flow
    };

    const features = [
        {
            icon: Zap,
            title: "Optimized Performance",
            description: "Lightning-fast WordPress hosting with optimized servers for maximum speed."
        },
        {
            icon: Shield,
            title: "Enhanced Security",
            description: "Advanced security features to protect your WordPress site from threats."
        },
        {
            icon: Server,
            title: "Auto Updates",
            description: "Automatic WordPress core, plugin, and theme updates for peace of mind."
        },
        {
            icon: Database,
            title: "Daily Backups",
            description: "Automated daily backups to ensure your data is always safe."
        },
        {
            icon: Globe,
            title: "Easy Migration",
            description: "Free WordPress site migration service to get you started quickly."
        },
        {
            icon: Check,
            title: "24/7 Support",
            description: "Expert WordPress support available around the clock."
        }
    ];

    const windowsServerFAQs = [
        {
            question: "What is Windows Server?",
            answer: "Once you've decided that a virtual private server (VPS) is the best option for you and your website, the next important thing is choosing the right operating system (OS) for your VPS.<br /><br />Windows Servers operate on one of the most popular OSes. This means it uses Microsoft solutions for database and website management. When you get Windows Server, your OS will automatically run on the latest Microsoft Hyper V version."
        },
        {
            question: "Windows Server vs. Linux Server: Which one is right for me?",
            answer: "Both operating systems are unique and cater to different types of users and market needs.<br /><br />Linux is an open-source OS, which means users can easily modify their server to suit their hosting needs. On the other hand, a Windows server uses a more robust system and provides a wide variety of built-in features.<br /><br />Since Linux is the more cost-saving option, it's a common choice for startup companies. Meanwhile, Windows is the standard option for most large businesses looking for a more user-friendly interface and seamless integration with Microsoft products."
        },
        {
            question: "How can I purchase a Windows Server?",
            answer: "To purchase a Windows Server, follow these steps:<br /><br />• In the Windows Server page, scroll down, and then select a plan.<br />• Select your service level and upgrades, then click \"Continue Order\".<br />• Under the \"Register & Payment\" tab, do either of the following:<br />&nbsp;&nbsp;&nbsp;&nbsp;◦ <strong>Returning Customer:</strong> Sign in to your account, then click \"Log In & Continue\".<br />&nbsp;&nbsp;&nbsp;&nbsp;◦ <strong>New Customer:</strong> Click \"Sign Up\", fill out the New Customer form, and then click \"Register & Continue\".<br />• Enter your payment details, then click \"Complete Order\".<br />• Click \"Finished\".<br /><br />Your order will be processed within 48 hours after payment has been confirmed."
        },
        {
            question: "How many sites can I host with my Windows Server?",
            answer: "There's no limit to the number of websites you can host on your VPS. Just make sure that the allocated resources of your plan cover your website's needs. If not, you can always upgrade your VPS plan to add more resources."
        },
        {
            question: "What can I install on my Windows Server?",
            answer: "You can install anything on your Windows Server as long as it does not violate our Terms of Service.<br /><br />Here are some of the things you can install on your Windows VPS:<br /><br />• Virtual Private Network (VPN)<br />• Media Server<br />• Web Hosting<br />• Email Hosting<br />• SSL Certificates<br />• Social Bookmarking Sites<br />• Really Simple Syndication (RSS)<br />• Voice Over Internet Protocol (VoIP service)<br />• Custom Scripts"
        }
    ];

    return (
        <>
            <SEO
                title="Windows Server - SAN micron"
                description="Discover powerful and flexible Windows Server hosting solutions with SAN micron. Perfect for businesses seeking reliable performance, robust security, and seamless integration with Microsoft technologies."
            />
            <div className="relative overflow-hidden min-h-screen text-white">
                <Breadcrumbs
                    title="Windows Server"
                    subtitle=""
                    breadcrumbText="Windows Server"
                    backgroundImage={BreadcrumbsImage}
                />

                {/* Hero Section */}
                <ParallaxSection speed={0.25}>
                    <Floating3D intensity={18}>
                        <section className="hosting-section container py-20 pt-32">
                            <div className="text-center max-w-4xl mx-auto">
                                {/* <h1 className="text-5xl md:text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-brandStart to-brandEnd">
                                    Microsoft Email Exchange
                                </h1> */}
                                <p className="text-xl text-textColor mb-8 leading-relaxed">
                                    Business-Class Emails for Your Own Domain Name.
                                </p>
                            </div>
                        </section>
                    </Floating3D>
                </ParallaxSection>

                {/* Pricing Plans */}
                <AnimatedSection delay={0.2} direction="up">
                    <div className="hosting-section">
                        {isDummyData ? (
                            <div className="container py-12">
                                <div className="max-w-2xl mx-auto">
                                    <div className="bg-yellow-500/10 border-2 border-yellow-500/50 rounded-lg p-6 flex items-start gap-4">
                                        <AlertTriangle className="w-6 h-6 text-yellow-500 flex-shrink-0 mt-0.5" />
                                        <div className="flex-1">
                                            <h3 className="text-yellow-400 font-bold text-lg mb-2">
                                                Using Fallback Data
                                            </h3>
                                            <p className="text-yellow-300/90 text-sm leading-relaxed">
                                                Unable to load plans from the API. Displaying sample data. Please check your API configuration or contact support to ensure real hosting plan data is available from sandomains.com.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        <ProductPlansDisplay
                            plans={plans}
                            loading={loading}
                            error={error}
                            currency={selectedCurrency}
                            onCurrencyChange={setSelectedCurrency}
                            onSelectPlan={handleSelectPlan}
                            buttonText="Get Started"
                            priceLabel="per month"
                        />
                    </div>
                </AnimatedSection>

                {/* Features Section */}
                <ParallaxSection speed={0.3} direction="down">
                    <AnimatedSection delay={0.3} direction="left">
                        <section className="hosting-section container py-20">
                            <div className="text-center mb-16">
                                <h2 className="text-4xl font-bold mb-4">Why Choose WordPress Hosting?</h2>
                                <p className="text-textColor text-lg">Everything you need for a powerful WordPress website</p>
                            </div>
                            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                                {features.map((feature, index) => {
                                    const Icon = feature.icon;
                                    return (
                                        <motion.div
                                            key={index}
                                            className="bg-cardBg/90 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-primary/30 transition-all"
                                            whileHover={{ scale: 1.02, y: -5 }}
                                            transition={{ type: "spring", stiffness: 300 }}
                                        >
                                            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-brandStart to-brandEnd flex items-center justify-center mb-4">
                                                <Icon className="w-6 h-6 text-white" />
                                            </div>
                                            <h3 className="text-xl font-bold mb-2">{feature.title}</h3>
                                            <p className="text-textColor">{feature.description}</p>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        </section>
                    </AnimatedSection>
                </ParallaxSection>

                {/* FAQ Section */}
                <AnimatedSection delay={0.6}>
                    <div className="abstract-bg hosting-section">
                        <FAQSection faqs={windowsServerFAQs} />
                    </div>
                </AnimatedSection>

                <AnimatedSection delay={0.7}>
                    <Footer />
                </AnimatedSection>
            </div>
        </>
    );
}

